import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Link,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { InspectionValues } from '@parallel-fluidics/shared/constants';
import { InventoryTxTypes } from '@parallel-fluidics/shared/models/inventory';
import { travelerFromFirestore } from '@parallel-fluidics/shared/models/travelers';
import recordInventoryAdjustment from 'features/inventory/apis/recordInventoryAdjustment';
import findProductByPart from 'features/products/apis/findProductByPart';
import ScanField from 'features/scan/components/ScanField';
import { ScanTypes } from 'features/scan/utils/parseScanCode';
import {
  doc,
  getDoc,
  getFirestore,
  updateDoc,
} from 'firebase/firestore';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

/**
 * Modal for manually adding or removing a traveler from inventory tracking.
 *
 * The QR scanner is shown immediately on open and hidden once a traveler is
 * scanned. "Scan another" resets state and brings the scanner back.
 *
 * One traveler is processed at a time (scan → review → act → scan another).
 *
 * Mirrors the updatedTravelerV2 cloud function logic:
 *   Add    → PRODUCED +1, sets productDocId/productVersionId on the traveler
 *   Remove → ADJUST   -1, clears productDocId/productVersionId on the traveler
 */
export default function ScanTravelerInventoryModal({
  open,
  onClose,
  onSuccess,
}) {
  const [travelerId, setTravelerId] = useState(null);
  const [travelerData, setTravelerData] = useState(null);
  const [productMatch, setProductMatch] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Cloudscape Modal renders via a React portal. On first open the portal
  // container may not be attached to the document yet when html5-qrcode's
  // render() fires its getElementById call. Delaying scanner mount by one
  // requestAnimationFrame ensures the portal is in the DOM.
  const [scannerReady, setScannerReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setScannerReady(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setScannerReady(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const reset = useCallback(() => {
    setTravelerId(null);
    setTravelerData(null);
    setProductMatch(null);
    setLoadError('');
    setSubmitError('');
    setSuccessMsg('');
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // ── Stable scan callback (stable identity prevents the camera from restarting) ──
  const handleScan = useCallback(async (id) => {
    reset();
    setTravelerId(id);
    setIsLoading(true);
    try {
      const db = getFirestore();
      const snap = await getDoc(doc(db, 'travelers', id));
      if (!snap.exists()) {
        setLoadError(`Traveler "${id}" not found.`);
        return;
      }
      const traveler = travelerFromFirestore(snap);
      setTravelerData(traveler);
      if (traveler.project && traveler.pPart) {
        const match = await findProductByPart(traveler.project, traveler.pPart);
        setProductMatch(match ?? null);
      }
    } catch (err) {
      setLoadError(err.message || 'Failed to load traveler');
    } finally {
      setIsLoading(false);
    }
  }, [reset]);

  // ── Inventory action ──────────────────────────────────────────────────────
  const handleAction = useCallback(async (action) => {
    setSubmitError('');
    setIsSubmitting(true);
    const db = getFirestore();
    const travelerRef = doc(db, 'travelers', travelerId);

    try {
      if (action === 'add') {
        await recordInventoryAdjustment({
          productDocId: productMatch.product.id,
          productVersionId: productMatch.version.id,
          sku: productMatch.product.sku,
          type: InventoryTxTypes.PRODUCED,
          deltaOnHand: 1,
          travelerIds: [travelerId],
          reason: 'Manual traveler scan — added to inventory',
        });
        await updateDoc(travelerRef, {
          productDocId: productMatch.product.id,
          productVersionId: productMatch.version.id,
        });
        setSuccessMsg(`Added ${travelerId.substring(0, 8)}… to inventory for ${productMatch.product.sku}.`);
      } else {
        const currentProductDocId = travelerData.productDocId;
        const currentProductVersionId = travelerData.productVersionId;
        let sku = productMatch?.product?.sku;
        if (!sku) {
          const productSnap = await getDoc(doc(db, 'products', currentProductDocId));
          sku = productSnap.exists() ? productSnap.data().sku : null;
          if (!sku) throw new Error('Could not resolve SKU for this traveler — product may have been deleted.');
        }
        await recordInventoryAdjustment({
          productDocId: currentProductDocId,
          productVersionId: currentProductVersionId,
          sku,
          type: InventoryTxTypes.ADJUST,
          deltaOnHand: -1,
          travelerIds: [travelerId],
          reason: 'Manual traveler scan — removed from inventory',
        });
        await updateDoc(travelerRef, {
          productDocId: null,
          productVersionId: null,
        });
        setSuccessMsg(`Removed ${travelerId.substring(0, 8)}… from inventory.`);
      }
      setTravelerData((prev) => ({
        ...prev,
        productDocId: action === 'add' ? productMatch.product.id : null,
        productVersionId: action === 'add' ? productMatch.version.id : null,
      }));
      onSuccess();
    } catch (err) {
      setSubmitError(err.message || 'Failed to update inventory');
    } finally {
      setIsSubmitting(false);
    }
  }, [travelerId, travelerData, productMatch, onSuccess]);

  // ── Derived display state ─────────────────────────────────────────────────
  const lastStep = travelerData?.steps?.length
    ? travelerData.steps[travelerData.steps.length - 1]
    : null;
  const lastStepPassed = lastStep?.inspected === InspectionValues.PASSED;
  const isTracked = !!travelerData?.productDocId;
  const canAdd = !isTracked
    && travelerData?.shipped === false
    && !!productMatch
    && lastStepPassed;
  const canRemove = isTracked && travelerData?.shipped === false;

  // Scanner is visible when no traveler has been scanned yet (or after reset).
  // scannerReady gates the first mount so the Modal portal is in the DOM.
  const showScanner = !travelerId && !isLoading && scannerReady;

  return (
    <Modal
      visible={open}
      onDismiss={handleClose}
      header="Scan traveler — update inventory"
      footer={(
        <Box float="right">
          <Button variant="link" onClick={handleClose}>Close</Button>
        </Box>
      )}
    >
      <SpaceBetween size="l">

        {/* Scanner — live on open, hidden once a traveler is scanned.
            scannerReady delays mount by one rAF so the Modal portal is in DOM. */}
        {showScanner && (
          <ScanField
            id="inventory-traveler-scanner"
            accept={[ScanTypes.TRAVELER]}
            onScan={({ id }) => handleScan(id)}
            cameraMode="inline"
            autoFocus
          />
        )}

        {isLoading ? <StatusIndicator type="loading">Loading traveler…</StatusIndicator> : null}
        {loadError ? (
          <SpaceBetween size="s">
            <Alert type="error">{loadError}</Alert>
            <Button onClick={reset}>Try again</Button>
          </SpaceBetween>
        ) : null}
        {successMsg ? (
          <Alert
            type="success"
            action={<Button onClick={reset}>Scan another</Button>}
          >
            {successMsg}
          </Alert>
        ) : null}
        {submitError ? <Alert type="error">{submitError}</Alert> : null}

        {/* Traveler detail + action — shown after scan, hidden during scanning */}
        {travelerData && !isLoading && (
          <SpaceBetween size="m">
            {productMatch ? (
              <ColumnLayout columns={2}>
                <div>
                  <Box variant="awsui-key-label">Matched product</Box>
                  <Box>{productMatch.product.name}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">SKU / Version</Box>
                  <Box>{`${productMatch.product.sku} · ${productMatch.version.id}`}</Box>
                </div>
              </ColumnLayout>
            ) : (
              <Alert type="warning">
                No catalog product found for this traveler&apos;s (project, pPart).
                Adding to inventory is not available.
              </Alert>
            )}

            <ColumnLayout columns={2}>
              <div>
                <Box variant="awsui-key-label">Traveler ID</Box>
                <Box>
                  <Link href={`/travelers/${travelerId}`} external>
                    {travelerId}
                  </Link>
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Project / Part</Box>
                <Box>{[travelerData.project, travelerData.pPart].filter(Boolean).join('-') || '—'}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Last step</Box>
                <Box>
                  {lastStep ? (
                    <StatusIndicator type={lastStepPassed ? 'success' : 'warning'}>
                      {lastStep.inspected || '—'}
                    </StatusIndicator>
                  ) : '—'}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Inventory state</Box>
                <Box>
                  {isTracked
                    ? <StatusIndicator type="success">Recorded</StatusIndicator>
                    : <StatusIndicator type="stopped">Not recorded</StatusIndicator>}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Shipped</Box>
                <Box>
                  {travelerData.shipped
                    ? <StatusIndicator type="success">Yes</StatusIndicator>
                    : <StatusIndicator type="pending">No</StatusIndicator>}
                </Box>
              </div>
            </ColumnLayout>

            {!lastStepPassed && !isTracked ? (
              <Alert type="warning">
                Last step is not passed -
                inventory can only be updated for travelers that have
                passed their last step inspection.
              </Alert>
            ) : null}

            <SpaceBetween direction="horizontal" size="s">
              <Button
                variant="primary"
                iconName="add-plus"
                disabled={!canAdd || isSubmitting}
                loading={isSubmitting}
                onClick={() => handleAction('add')}
              >
                Add to inventory (+1 produced)
              </Button>
              <Button
                variant="normal"
                iconName="remove"
                disabled={!canRemove || isSubmitting}
                loading={isSubmitting}
                onClick={() => handleAction('remove')}
              >
                Remove from inventory (−1)
              </Button>
            </SpaceBetween>

            <Box>
              <Button variant="inline-link" onClick={reset}>← Scan a different traveler</Button>
            </Box>
          </SpaceBetween>
        )}

      </SpaceBetween>
    </Modal>
  );
}

ScanTravelerInventoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
